import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCpu, FiAlertTriangle, FiCheckCircle, FiTrendingUp, FiShield, FiZap, FiList } = FiIcons;

const AIInsights = ({ goals, userProfile, totalRequiredSIP }) => {
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState([]);
  const [score, setScore] = useState(0);

  useEffect(() => {
    // Simulate AI processing time
    const timer = setTimeout(() => {
      generateInsights();
      setLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, [goals, userProfile]);

  const generateInsights = () => {
    const newInsights = [];
    let healthScore = 100;

    // 1. Analyze Short-term Risk (Equity in Short-term)
    const shortTermRiskyGoals = goals.filter(g => 
      parseInt(g.yearsFromNow) <= 3 && parseFloat(g.expectedReturn) > 9
    );

    if (shortTermRiskyGoals.length > 0) {
      healthScore -= 20;
      newInsights.push({
        type: 'critical',
        icon: FiAlertTriangle,
        title: 'High Volatility Risk Detected',
        message: `You have ${shortTermRiskyGoals.length} goal(s) due in < 3 years with high return expectations (>9%). Equity markets can be volatile in the short term.`,
        recommendation: 'Consider shifting these to Debt/Liquid funds to protect capital, even if returns are lower.'
      });
    }

    // 2. Analyze Long-term Opportunity (Debt in Long-term)
    const longTermConservativeGoals = goals.filter(g => 
      parseInt(g.yearsFromNow) > 7 && parseFloat(g.expectedReturn) < 10
    );

    if (longTermConservativeGoals.length > 0) {
      healthScore -= 15;
      newInsights.push({
        type: 'warning',
        icon: FiTrendingUp,
        title: 'Inflation Risk on Long-term Goals',
        message: `Some long-term goals (>7 years) have low return expectations (<10%). Conservative investments may not beat education/medical inflation.`,
        recommendation: 'For 7+ year goals, equity mutual funds usually offer better inflation-adjusted returns.'
      });
    }

    // 3. SIP Gap Analysis
    const currentTotalSIP = goals.reduce((sum, g) => sum + (g.currentlyInvesting ? parseFloat(g.currentSIP || 0) : 0), 0);
    const sipGap = totalRequiredSIP - currentTotalSIP;
    const gapPercentage = totalRequiredSIP > 0 ? (sipGap / totalRequiredSIP) * 100 : 0;

    if (gapPercentage > 50) {
      healthScore -= 25;
      newInsights.push({
        type: 'warning',
        icon: FiZap,
        title: 'Significant Investment Gap',
        message: `You are currently investing only ${Math.round((currentTotalSIP/totalRequiredSIP)*100)}% of the required amount.`,
        recommendation: 'Start with what you can, and use a "Step-up SIP" to increase contribution by 10% every year.'
      });
    } else if (gapPercentage <= 0) {
      newInsights.push({
        type: 'success',
        icon: FiCheckCircle,
        title: 'On Track!',
        message: 'Your current planned investments cover your goal requirements.',
        recommendation: 'Review annually to ensure you stay ahead of inflation.'
      });
    }

    // 4. Inflation Reality Check
    const educationGoals = goals.filter(g => g.name.toLowerCase().includes('education'));
    if (educationGoals.length > 0) {
      newInsights.push({
        type: 'info',
        icon: FiShield,
        title: 'Education Inflation Factor',
        message: 'Education cost inflation (10-12%) is typically higher than general inflation (6%).',
        recommendation: 'Ensure your education goals assume a higher future cost than standard calculations.'
      });
    }

    // 5. Emergency Fund Check
    const hasEmergencyFund = goals.some(g => g.name.toLowerCase().includes('emergency'));
    if (!hasEmergencyFund) {
      healthScore -= 10;
      newInsights.push({
        type: 'info',
        icon: FiShield,
        title: 'Missing Emergency Fund',
        message: 'No "Emergency Fund" goal detected.',
        recommendation: 'Before aggressive investing, ensure you have 6 months of expenses in a Liquid Fund.'
      });
    }

    setInsights(newInsights);
    setScore(Math.max(0, healthScore));
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-8 mb-8 border border-indigo-100 flex flex-col items-center justify-center min-h-[300px]">
        <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mb-4"></div>
        <h3 className="text-xl font-semibold text-gray-800 animate-pulse">AI Analyst is reviewing your portfolio...</h3>
        <p className="text-gray-500 mt-2">Checking asset allocation, risk factors, and inflation impact.</p>
      </div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="bg-gradient-to-br from-indigo-900 via-purple-900 to-indigo-800 text-white rounded-xl shadow-xl overflow-hidden mb-8"
    >
      <div className="p-6 border-b border-indigo-700/50 flex flex-col md:flex-row justify-between items-center gap-4">
        <div className="flex items-center space-x-3">
          <div className="bg-indigo-500/20 p-3 rounded-lg backdrop-blur-sm">
            <SafeIcon icon={FiCpu} className="text-3xl text-cyan-300" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">AI Portfolio Insights</h2>
            <p className="text-indigo-200 text-sm">Automated analysis based on your goals & inputs</p>
          </div>
        </div>
        <div className="flex items-center space-x-4 bg-black/20 px-6 py-3 rounded-lg backdrop-blur-sm border border-indigo-500/30">
          <div className="text-right">
            <div className="text-xs text-indigo-300 uppercase tracking-wider font-semibold">Plan Health Score</div>
            <div className={`text-2xl font-bold ${score >= 80 ? 'text-green-400' : score >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>
              {score}/100
            </div>
          </div>
          <div className="h-10 w-10 text-white flex items-center justify-center">
            <SafeIcon icon={FiList} className="text-2xl" />
          </div>
        </div>
      </div>

      <div className="p-6 grid md:grid-cols-2 gap-6">
        {insights.map((insight, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`rounded-lg p-4 border backdrop-blur-sm ${
              insight.type === 'critical' ? 'bg-red-500/10 border-red-500/30' :
              insight.type === 'warning' ? 'bg-orange-500/10 border-orange-500/30' :
              insight.type === 'success' ? 'bg-green-500/10 border-green-500/30' :
              'bg-blue-500/10 border-blue-500/30'
            }`}
          >
            <div className="flex items-start space-x-3">
              <SafeIcon 
                icon={insight.icon} 
                className={`text-xl mt-1 flex-shrink-0 ${
                  insight.type === 'critical' ? 'text-red-400' :
                  insight.type === 'warning' ? 'text-orange-400' :
                  insight.type === 'success' ? 'text-green-400' :
                  'text-blue-400'
                }`} 
              />
              <div>
                <h4 className={`font-semibold mb-1 ${
                  insight.type === 'critical' ? 'text-red-200' :
                  insight.type === 'warning' ? 'text-orange-200' :
                  insight.type === 'success' ? 'text-green-200' :
                  'text-blue-200'
                }`}>
                  {insight.title}
                </h4>
                <p className="text-indigo-100/80 text-sm mb-3 leading-relaxed">
                  {insight.message}
                </p>
                <div className={`text-xs px-3 py-2 rounded ${
                  insight.type === 'critical' ? 'bg-red-900/40 text-red-200' :
                  insight.type === 'warning' ? 'bg-orange-900/40 text-orange-200' :
                  insight.type === 'success' ? 'bg-green-900/40 text-green-200' :
                  'bg-blue-900/40 text-blue-200'
                }`}>
                  <strong>💡 Recommendation:</strong> {insight.recommendation}
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

export default AIInsights;